import React, { FC, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import Button from "../../components/Button";
import SectionLabel from "../../components/SectionLabel";
import EditProfileModal from "../../components/EditProfileModal";
import ChangePasswordModal from "./components/ChangePasswordModal";
import DeleteAccountModal from "../../components/DeleteAccountModal";
import Card from "../../components/Card";
import Grid, { Col, Row } from "../../components/Grid";
import userAPIService from "../../services/api/user";
import { IUserUpdatePayload } from "../../services/api/user/types";
import ProfileCard from "../../components/ProfileCard";
import Toaster from "../../services/toaster";
import { selectOriginSettings } from "../../store/metadata";
import {
  fetchCurrentPrincipal,
  selectCustomerData,
  selectPrincipalData,
} from "../../store/user";
import { getExistErrorKey } from "../../utils/common/errors";
import { useUserManager } from "../../services/auth";
import { IErrorDetail } from "../../utils/common/types";
import {
  RESPONSE_ERROR_KEY,
  RESPONSE_ERROR_PROBLEM,
} from "../../utils/common/contants";
import { OWNER } from "../../services/api/user/constants";

import "./styles.scss";

export const Profile: FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const userData = useSelector(selectPrincipalData);
  const customerData = useSelector(selectCustomerData);
  const originSettings = useSelector(selectOriginSettings);
  const isCorporate = customerData?.is_corporate;
  const userManager = useUserManager();
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showChangePassDialog, setShowChangePassDialog] = useState(false);
  const [showDeleteAccountDialog, setShowDeleteAccountDialog] = useState(false);
  const [errors, setErrors] = useState<Record<string, string> | undefined>();
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const isEmailAvailable = useMemo(() => {
    return originSettings?.principal_email_synchronized_from_username === false;
  }, [originSettings]);
  const isUsernameDisabled = useMemo(() => {
    return originSettings?.principal_username_is_immutable === true;
  }, [originSettings]);

  const handleProfileEditClick = () => {
    return setShowEditDialog(true);
  };

  const handleCloseEditProfileModal = () => {
    setErrors({});
    return setShowEditDialog(false);
  };

  const handleCloseDeleteAccountModal = () => {
    setErrors({});
    return setShowDeleteAccountDialog(false);
  };

  const handleChangePassClick = () => {
    return setShowChangePassDialog(!showChangePassDialog);
  };

  const handleDeleteAccountClick = () => {
    return setShowDeleteAccountDialog(!showDeleteAccountDialog);
  };

  const handleEditProfileSubmit = async (
    principalData: IUserUpdatePayload,
    businessName: string,
  ) => {
    const receivedErrors: Record<string, string> = {};
    if (!userData?.id) {
      return;
    }

    try {
      setIsFetching(true);
      setErrors(undefined);
      if (isCorporate && userData?.customer_role === OWNER) {
        try {
          await userAPIService.editCustomer(customerData?.id, {
            name: businessName,
          });
        } catch (error) {
          const errorDetails = error?.response?.data?.details;

          errorDetails?.forEach((errorDetail: IErrorDetail) => {
            if (error?.response?.status === 422) {
              if (
                errorDetail.path === "name" &&
                errorDetail.problem === RESPONSE_ERROR_PROBLEM.OutOfRange
              ) {
                receivedErrors.businessName = "BusinessNameIsTooLong";
              }
            }
          });

          setErrors((state) => {
            return {
              ...state,
              ...receivedErrors,
            };
          });
        }
      }
      await userAPIService.update(principalData);

      if (!Object.values(receivedErrors).length) {
        if (userData?.username !== principalData.username) {
          userManager.signoutRedirect();
          return;
        }

        dispatch(fetchCurrentPrincipal(userData?.id));
        handleCloseEditProfileModal();
      }
    } catch (error) {
      const errorDetails = error?.response?.data?.details;
      let isHandledError = false;

      if (!errorDetails) {
        receivedErrors.password = RESPONSE_ERROR_KEY.Illegal;
        isHandledError = true;
      }

      errorDetails?.forEach((errorDetail: IErrorDetail) => {
        if (error?.response?.status === 422) {
          if (
            errorDetail.path === "email" &&
            errorDetail.problem === RESPONSE_ERROR_PROBLEM.Illegal
          ) {
            receivedErrors.email = "latinCharactersOnly";
            isHandledError = true;
          }

          if (
            errorDetail.path === "email" &&
            (errorDetail.problem === RESPONSE_ERROR_PROBLEM.InvalidValue ||
              errorDetail.problem === RESPONSE_ERROR_PROBLEM.OutOfRange)
          ) {
            receivedErrors.email = "InvalidEmailFormat";
            isHandledError = true;
          }

          if (
            errorDetail.path === "name" &&
            (errorDetail.problem === RESPONSE_ERROR_PROBLEM.InvalidValue ||
              errorDetail.problem === RESPONSE_ERROR_PROBLEM.OutOfRange)
          ) {
            receivedErrors.name = "NameIsTooLong";
            isHandledError = true;
          }

          if (
            errorDetail.path === "current_password" &&
            errorDetail.problem === RESPONSE_ERROR_PROBLEM.PasswordMismatch
          ) {
            receivedErrors.password = "InvalidCurrentPassword";
            isHandledError = true;
          }
        }

        if (error?.response?.status === 409 && userData) {
          isHandledError = true;
          Toaster.error({
            content: t(getExistErrorKey(errorDetail)),
          });
        }
      });

      setErrors((state) => {
        return { ...state, ...receivedErrors };
      });

      if (isHandledError) {
        return;
      }

      Toaster.error({
        content: t("somethingWentWrong"),
      });
    } finally {
      setIsFetching(false);
    }
  };

  const handleDeleteAccountSubmit = async (currentPassword: string) => {
    const receivedErrors: Record<string, string> = {};
    if (!userData?.id) {
      return;
    }

    try {
      setIsFetching(true);
      setErrors(undefined);
      let isSuccessful = false;

      try {
        await userAPIService.delete(customerData?.id, currentPassword);
        isSuccessful = true;
      } catch (error) {
        const errorDetails = error?.response?.data?.details;
        let isHandledError = false;

        if (!errorDetails) {
          receivedErrors.password = RESPONSE_ERROR_KEY.Illegal;
          isHandledError = true;
        }

        errorDetails?.forEach((errorDetail: IErrorDetail) => {
          if (error?.response?.status === 422) {
            if (
              errorDetail.path === "password" &&
              errorDetail.problem === RESPONSE_ERROR_PROBLEM.PasswordMismatch
            ) {
              receivedErrors.password = "InvalidCurrentPassword";
              isHandledError = true;
            }
          }
        });

        setErrors((state) => {
          return {
            ...state,
            ...receivedErrors,
          };
        });

        if (isHandledError) {
          return;
        }

        Toaster.error({
          content: t("somethingWentWrong"),
        });
      }

      if (!Object.values(receivedErrors).length && isSuccessful) {
        userManager.signoutRedirect();
      }
    } catch (error) {
      Toaster.error({
        content: t("somethingWentWrong"),
      });
    } finally {
      setIsFetching(false);
    }
  };

  if (!userData) {
    return null;
  }

  return (
    <section className="ams-profile">
      <header>
        <h1 className="page-title">{t("profile")}</h1>
      </header>
      <ProfileCard
        isShowEmail={isEmailAvailable}
        titleText={t("profileInfo")}
        actionText={t("updateProfileInfo")}
        name={userData.name}
        username={userData.username}
        businessName={customerData?.is_corporate ? customerData?.name : ""}
        email={userData.email}
        brandCode={userData.tenant_code}
        handleAction={handleProfileEditClick}
      />
      <Card>
        <h2>{t("password")}</h2>
        <Grid>
          <Row>
            <Col>
              <SectionLabel>{t("password")}</SectionLabel>
            </Col>
            <Col className="ams-col__value">
              <SectionLabel>••••••••••••</SectionLabel>
            </Col>
          </Row>
        </Grid>
        <Button onClick={handleChangePassClick}>{t("changePassword")}</Button>
      </Card>
      <Card>
        <h2>{t("accountDeletion")}</h2>
        <Grid>
          <Row>
            <Col>
              <Button onClick={handleDeleteAccountClick}>
                {t("deleteAccount")}
              </Button>
            </Col>
          </Row>
        </Grid>
      </Card>
      {showEditDialog && (
        <EditProfileModal
          isUserNameDisabled={isUsernameDisabled}
          isSyncEmail={!isEmailAvailable}
          isOpen={showEditDialog}
          errors={errors}
          user={userData}
          businessName={customerData?.is_corporate ? customerData?.name : ""}
          isLoading={isFetching}
          onDismiss={handleCloseEditProfileModal}
          onSubmit={handleEditProfileSubmit}
        />
      )}
      {showChangePassDialog && (
        <ChangePasswordModal
          isOpen={showChangePassDialog}
          onDismiss={handleChangePassClick}
          user={userData}
        />
      )}
      {showDeleteAccountDialog && !isCorporate && (
        <DeleteAccountModal
          isOpen={showDeleteAccountDialog}
          errors={errors}
          onDismiss={handleCloseDeleteAccountModal}
          onSubmit={handleDeleteAccountSubmit}
          isLoading={isFetching}
          user={userData}
        />
      )}
    </section>
  );
};
